import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import {
  Enterprise,
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  ENTERPRISE_MEMBER_STATUS,
  EnterpriseMember,
  UpdateEnterpriseMember,
} from 'models/Enterprises';
import { FormApi } from 'final-form';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import validations from 'validations';
import { SUCCESS_MESSAGES } from 'request-responses';
import SelectField from 'components/SelectField';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import DropzoneField from 'components/DropzoneField';
import { Button, Loader } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { BasicUserInfo } from 'models/User';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import useBusinessUnits from 'hooks/useBusinessUnits';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import EmailField from 'components/EmailField';
import useMemberSeniorities from 'hooks/useMemberSeniorities';
import useMemberLocations from 'hooks/useMemberLocations';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useMatchMedia from 'hooks/useMatchMedia';

import './ManageMemberDrawer.styles.scss';

const FIELDS_TO_UPDATE: (keyof BasicUserInfo)[] = [
  'firstName',
  'lastName',
  'phone',
  'linkedinAccount',
  'title',
  'profileImage',
];

type ManageMemberDrawerProps = {
  className?: string;
  member?: EnterpriseMember;
  enterprise: Partial<Enterprise>;
  onClose: () => void;
  onSuccess: (member: EnterpriseMember) => void;
};

const ManageMemberDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageMemberDrawerProps
> = (props, ref) => {
  const { className, member, enterprise, onSuccess, onClose } = props;

  const { brandName, memberSeniorities, memberLocations, businessUnits } =
    enterprise || {};
  const { _id, status, invitationStatus, existUser } = member || {};

  const [isDeletingMember, setIsDeletingMember] = useState(false);

  const sendInvitationRef = useRef(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { isOfficeManager, enterpriseMember } = useContext(CurrentUserContext);
  const { isRemoveDisabled, deleteMembers } = useContext(
    EnterpriseMembersContext,
  );

  const formRef = useRef<FormApi<
    UpdateEnterpriseMember,
    Partial<UpdateEnterpriseMember>
  > | null>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('manage-member-drawer', className);

  const {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    prefilledFields,
    onEmailChange,
    resetState,
  } = useUserBasicInfo();

  const shouldPublish = status === ENTERPRISE_MEMBER_STATUS.DRAFTED;
  const canInvite =
    invitationStatus === ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED;

  const disabledUserData = isUserBasicInfoSet || existUser;

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const isPhablet = useMatchMedia('isPhablet');

  const { businessUnitsOptions } = useBusinessUnits(businessUnits);
  const { memberSenioritiesOptions } = useMemberSeniorities(memberSeniorities);
  const { memberLocationsOptions } = useMemberLocations(memberLocations);

  const initialValues = useMemo(
    () => ({
      ...member,
      phone: member?.user?.phone,
      linkedinAccount: member?.user?.linkedinAccount,
      ...(isOfficeManager && {
        enterpriseBusinessUnitId: enterpriseMember?.enterpriseBusinessUnitId,
      }),
    }),
    [enterpriseMember?.enterpriseBusinessUnitId, isOfficeManager, member],
  );

  const clearStates = useCallback(() => {
    resetState();
    formRef.current?.restart();
    formRef.current?.reset();
  }, [resetState]);

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    clearStates();
    onClose();
  }, [clearStates, drawer, onClose]);

  const onDeleteMember = useCallback(async () => {
    try {
      setIsDeletingMember(true);

      await deleteMembers([member]);

      handleOnClose();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDeletingMember(false);
    }
  }, [member, handleOnClose, popServerError, deleteMembers]);

  const handleOnSubmit = useCallback(
    async (values: UpdateEnterpriseMember) => {
      const {
        firstName,
        lastName,
        title,
        email,
        phone,
        linkedinAccount,
        enterpriseSeniorityId,
        enterpriseBusinessUnitId,
        enterpriseLocationId,
        isOfficeManager,
      } = values;

      try {
        const apiCall = shouldPublish
          ? api.enterprises.addEnterprisePerson
          : api.enterprises.updateEnterprisePerson;

        const { data } = await apiCall(_id, {
          firstName,
          lastName,
          title,
          phone,
          enterpriseSeniorityId,
          enterpriseBusinessUnitId,
          enterpriseLocationId,
          isOfficeManager,
          linkedinAccount: linkedinAccount || null,
          sendInvitation: sendInvitationRef.current,
          ...(shouldPublish && { email }),
        });

        sendInvitationRef.current = false;

        popSuccess(
          t(
            shouldPublish
              ? SUCCESS_MESSAGES.CREATE_MEMBER
              : SUCCESS_MESSAGES.EDIT_MEMBER,
          ),
        );

        onSuccess?.(data);
        handleOnClose();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      shouldPublish,
      _id,
      popSuccess,
      t,
      onSuccess,
      handleOnClose,
      popServerError,
    ],
  );

  const uploadProfileImage: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const data = await api.enterprises.uploadProfileImage(
        file,
        fileName,
        _id,
        config,
      );

      return data;
    },
    [_id],
  );

  const deleteProfileImage: DropzoneDelete = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId },
      } = await api.enterprises.deleteProfileImage(fileName, file._id, _id);
      return fileId;
    },
    [_id],
  );

  const validate = (values: Partial<UpdateEnterpriseMember>) => {
    const { title } = values;

    if (!disabledUserData) {
      return { title: commonValidations.name(title, values) };
    }

    return {};
  };

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      closeAction={handleOnClose}
      openAction={clearStates}
    >
      {!member ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          validate={validate}
          render={({ submitting, invalid, form, values, handleSubmit }) => {
            formRef.current = form;

            const isDeleteButtonDisabled = isDeletingMember || isRemoveDisabled;
            const isSubmitDisabled =
              invalid || submitting || isFetchingUserBasicInfo;

            return (
              <form onSubmit={handleSubmit}>
                <p>
                  {t(
                    shouldPublish
                      ? 'addEnterprisePeople'
                      : 'manageEnterprisePeople',
                    {
                      brandName,
                    },
                  )}
                </p>

                {disabledUserData && (
                  <p className="warning">
                    <WarningExclamationIcon />
                    {t('thisUserAlreadyHasAccount')}
                  </p>
                )}

                <div>
                  <Field
                    name="firstName"
                    label={t('firstName')}
                    component={InputField}
                    disabled={prefilledFields.firstName || existUser}
                    required
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    label={t('lastName')}
                    component={InputField}
                    disabled={prefilledFields.lastName || existUser}
                    required
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField
                  required
                  disabled={!shouldPublish}
                  onChangeHandle={(email) =>
                    onEmailChange({
                      email,
                      form,
                      fieldsToPrefill: FIELDS_TO_UPDATE,
                    })
                  }
                />

                <PhoneFieldWrapper
                  disabled={prefilledFields.phone || existUser}
                />

                <Field
                  name="title"
                  label={t('professionalTitle')}
                  component={InputField}
                  disabled={prefilledFields.title || existUser}
                  {...(!disabledUserData && {
                    required: true,
                  })}
                />

                <Field
                  name="enterpriseSeniorityId"
                  label={t('seniority')}
                  options={memberSenioritiesOptions}
                  placeholder=""
                  component={SelectField}
                  validate={validations.required()}
                  key={values.enterpriseSeniorityId}
                  required
                  searchable
                  renderAsPortal={isPhablet}
                />

                <Field
                  name="isOfficeManager"
                  type="checkbox"
                  component={CheckboxField}
                  className="office-manager-field"
                  label={
                    <div>
                      <span>{t('thisUserIsAnOfficeManager')}</span>
                      <ResolvePopup
                        onHover
                        className="office-manager-field__info-icon"
                        popupContent={t('thisUserIsAnOfficeManagerTooltip')}
                      >
                        <InformationcircleIcon />
                      </ResolvePopup>
                    </div>
                  }
                />

                <Field
                  name="enterpriseBusinessUnitId"
                  label={t('businessUnit')}
                  options={businessUnitsOptions}
                  placeholder=""
                  component={SelectField}
                  validate={validations.required()}
                  key={values.enterpriseBusinessUnitId}
                  required
                  searchable
                  renderAsPortal={isPhablet}
                  {...(isOfficeManager && { disabled: true })}
                />

                <Field
                  name="enterpriseLocationId"
                  label={t('location')}
                  options={memberLocationsOptions}
                  placeholder=""
                  key={values.enterpriseLocationId}
                  component={SelectField}
                  validate={validations.required()}
                  required
                  searchable
                  renderAsPortal={isPhablet}
                />

                <Field
                  name="linkedinAccount"
                  label={t('linkedin')}
                  disabled={prefilledFields.linkedinAccount || existUser}
                  component={InputField}
                  validate={validations.linkedinValidation()}
                />

                <Field
                  accept="image/*"
                  maxFiles={1}
                  label={t('profileImage')}
                  name="profileImage"
                  disabled={prefilledFields.profileImage || existUser}
                  description={t('weAcceptPngJpegJpgWebp')}
                  component={DropzoneField}
                  uploadRequest={uploadProfileImage}
                  deleteRequest={deleteProfileImage}
                />

                <div className="actions">
                  {!shouldPublish && (
                    <Button
                      type="button"
                      className="alert"
                      disabled={isDeleteButtonDisabled}
                      onClick={onDeleteMember}
                    >
                      {t('removeFromEnterprise')}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={isSubmitDisabled}
                    {...(canInvite && { variant: 'link' })}
                  >
                    {t(shouldPublish ? 'add' : 'save')}
                  </Button>
                  {canInvite && (
                    <Button
                      type="submit"
                      disabled={isSubmitDisabled}
                      onClick={() => (sendInvitationRef.current = true)}
                    >
                      {t(shouldPublish ? 'addAndInvite' : 'saveAndInvite')}
                    </Button>
                  )}
                </div>

                {(isDeletingMember || submitting) && <Loader />}
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageMemberDrawer);
